import { ImageMetadata, defaultImgMetadata } from 'modules/creativeLibrary';
import { useEffect, useState } from 'react';
import { getFileName } from 'utils/urls';

import { useTranslation } from '../../../hooks/useTranslation';
import { useContentMetadata } from '../contexts/ContentMetadata';

const loadImage = (imageSrc: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const imageElement = new Image();
    imageElement.onload = () => resolve(imageElement);
    imageElement.onerror = () => reject();
    imageElement.src = imageSrc;
  });

export const useImage = (
  imageSrc: string | null,
): { isLoading: boolean; didError: boolean; imgMetadata: ImageMetadata } => {
  const { t } = useTranslation();
  const { imageMetadata } = useContentMetadata();
  const fileName = imageSrc ? getFileName(imageSrc) : t('contentPicker.unknownFilename');
  const [didError, setDidError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [imgMetadata, setImgMetadata] = useState<ImageMetadata>(defaultImgMetadata);

  useEffect(() => {
    if (imageSrc) {
      setIsLoading(true);
      loadImage(imageSrc)
        .then((image) => {
          setDidError(false);
          setImgMetadata({
            height: image.naturalHeight,
            width: image.naturalWidth,
          });
        })
        .catch(() => {
          setDidError(true);
          setImgMetadata(defaultImgMetadata);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [imageSrc]);

  return {
    isLoading,
    didError,
    imgMetadata: {
      assetName: fileName || undefined,
      ...imgMetadata,
      ...imageMetadata,
    },
  };
};
